import React, { useCallback, useMemo } from 'react';
import { FieldLabel } from '@puckeditor/core';

import { isSurfaceColorRole, resolveSurfaceColor } from '../tokens';
import type { SurfaceColorRole } from '../tokens';
import { segmentStyle, sanitizeHexInput } from './color-role-field';

export type BackgroundValue = {
  color: string;
  image: string;
  size: string;
  position: string;
  repeat: string;
};

export const DEFAULT_BACKGROUND: BackgroundValue = {
  // 'surface' resolves to `--dcm-color-surface` (fallback #ffffff) — no visual change,
  // but the default now follows the merchant theme rather than a baked-in hex.
  color: 'surface',
  image: '',
  size: 'cover',
  position: 'center',
  repeat: 'no-repeat',
};

const SIZE_OPTIONS = [
  { label: 'Cover', value: 'cover' },
  { label: 'Contain', value: 'contain' },
  { label: 'Auto', value: 'auto' },
] as const;

const POSITION_OPTIONS = [
  { label: 'Center', value: 'center' },
  { label: 'Top', value: 'top' },
  { label: 'Bottom', value: 'bottom' },
  { label: 'Left', value: 'left' },
  { label: 'Right', value: 'right' },
  { label: 'Top Left', value: 'top left' },
  { label: 'Top Right', value: 'top right' },
  { label: 'Bottom Left', value: 'bottom left' },
  { label: 'Bottom Right', value: 'bottom right' },
] as const;

const REPEAT_OPTIONS = [
  { label: 'No Repeat', value: 'no-repeat' },
  { label: 'Repeat', value: 'repeat' },
  { label: 'Repeat X', value: 'repeat-x' },
  { label: 'Repeat Y', value: 'repeat-y' },
] as const;

/** Starting colour when the author switches from a theme role to a custom hex. */
const CUSTOM_DEFAULT = '#f5f6f7';

/** Role buttons offered by the background colour picker. */
const SURFACE_ROLE_LABELS: Record<SurfaceColorRole, string> = {
  none: 'None',
  surface: 'Surface',
  muted: 'Muted',
};

const SURFACE_ROLE_KEYS = Object.keys(SURFACE_ROLE_LABELS) as SurfaceColorRole[];

const COMPLETE_HEX = /^#[0-9a-fA-F]{6}$/;

/** Converts a BackgroundValue into React inline style properties. */
export function backgroundStyle(bg: BackgroundValue | undefined): React.CSSProperties {
  if (!bg) return { backgroundColor: resolveSurfaceColor(DEFAULT_BACKGROUND.color) };

  const style: React.CSSProperties = { backgroundColor: resolveSurfaceColor(bg.color) };
  if (bg.image) {
    const escapedUrl = bg.image.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    style.backgroundImage = `url("${escapedUrl}")`;
    style.backgroundSize = bg.size || DEFAULT_BACKGROUND.size;
    style.backgroundPosition = bg.position || DEFAULT_BACKGROUND.position;
    style.backgroundRepeat = bg.repeat || DEFAULT_BACKGROUND.repeat;
  }
  return style;
}

/** AI schema for the BackgroundValue object. */
export const BACKGROUND_AI_SCHEMA = {
  type: 'object',
  required: ['color'],
  properties: {
    color: {
      type: 'string',
      description:
        "Either a surface theme role ('none', 'surface', 'muted') or a hex value such as #ff0000. Prefer a role so the background follows merchant branding.",
    },
    image: { type: 'string' },
    size: { type: 'string', enum: ['cover', 'contain', 'auto'] },
    position: {
      type: 'string',
      enum: ['center', 'top', 'bottom', 'left', 'right', 'top left', 'top right', 'bottom left', 'bottom right'],
    },
    repeat: { type: 'string', enum: ['no-repeat', 'repeat', 'repeat-x', 'repeat-y'] },
  },
};

type BackgroundFieldProps = {
  field: { label?: string };
  name: string;
  value: BackgroundValue | undefined;
  onChange: (value: BackgroundValue) => void;
  readOnly?: boolean;
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 8px',
  border: '1px solid #d0d4d8',
  borderRadius: 4,
  fontSize: 14,
  boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  background: '#fff',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginTop: 8,
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#646d72',
  fontWeight: 500,
  minWidth: 52,
};

/** Composite Puck field widget for background styling (color + optional image settings). */
export function BackgroundField({ field, name, value, onChange, readOnly }: BackgroundFieldProps): JSX.Element {
  const bg = useMemo(() => ({ ...DEFAULT_BACKGROUND, ...value }), [value]);

  const isCustom = !isSurfaceColorRole(bg.color);

  const handleSelectRole = useCallback((role: SurfaceColorRole) => onChange({ ...bg, color: role }), [bg, onChange]);

  const handleSelectCustom = useCallback(() => {
    // Only seed the starting hex when not already in custom mode.
    if (!isCustom) onChange({ ...bg, color: CUSTOM_DEFAULT });
  }, [isCustom, bg, onChange]);

  const handleHexChange = useCallback(
    // Deliberate fix: previously handleColor persisted raw input verbatim, so pasted
    // arbitrary text would be stored and later emitted as a CSS value — the same hole
    // sanitizeHexInput's doc comment describes for text colour. Route through the
    // sanitizer so only valid hex characters reach the store.
    (e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...bg, color: sanitizeHexInput(e.currentTarget.value) }),
    [bg, onChange]
  );

  const handleImage = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...bg, image: e.currentTarget.value }),
    [bg, onChange]
  );

  const handleSize = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => onChange({ ...bg, size: e.currentTarget.value }),
    [bg, onChange]
  );

  const handlePosition = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => onChange({ ...bg, position: e.currentTarget.value }),
    [bg, onChange]
  );

  const handleRepeat = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => onChange({ ...bg, repeat: e.currentTarget.value }),
    [bg, onChange]
  );

  return (
    <FieldLabel label={field.label ?? name} readOnly={readOnly}>
      {/* Color role row — semantic roles first, Custom as escape hatch. Keeps the labelled-row
          shape of the Image/Size/Position/Repeat rows below so the label column stays aligned. */}
      <div style={rowStyle}>
        <span style={labelStyle}>Color</span>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', flex: 1 }}>
          {SURFACE_ROLE_KEYS.map((role) => (
            <button
              key={role}
              type='button'
              disabled={readOnly}
              aria-pressed={bg.color === role}
              onClick={() => handleSelectRole(role)}
              data-testid={`bg-color-role-${name}-${role}`}
              style={segmentStyle(bg.color === role)}
            >
              {SURFACE_ROLE_LABELS[role]}
            </button>
          ))}
          <button
            type='button'
            disabled={readOnly}
            aria-pressed={isCustom}
            onClick={handleSelectCustom}
            data-testid={`bg-color-role-${name}-custom`}
            style={segmentStyle(isCustom)}
          >
            Custom
          </button>
        </div>
      </div>

      {/* Custom hex inputs — only shown when author has chosen a custom colour */}
      {isCustom && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
          <input
            type='color'
            value={COMPLETE_HEX.test(bg.color) ? bg.color : CUSTOM_DEFAULT}
            onChange={handleHexChange}
            disabled={readOnly}
            aria-label={`${field.label ?? name} custom colour swatch`}
            data-testid={`bg-color-swatch-${name}`}
            style={{
              width: 32,
              height: 32,
              padding: 0,
              border: '1px solid #d0d4d8',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          />
          <input
            type='text'
            value={bg.color}
            onChange={handleHexChange}
            disabled={readOnly}
            aria-label={`${field.label ?? name} custom colour value`}
            data-testid={`bg-color-text-${name}`}
            style={{ ...inputStyle, flex: 1, fontFamily: 'monospace' }}
          />
        </div>
      )}

      {/* Image URL row */}
      <div style={rowStyle}>
        <span style={labelStyle}>Image</span>
        <input
          type='text'
          value={bg.image}
          onChange={handleImage}
          disabled={readOnly}
          placeholder='https://...'
          data-testid={`bg-image-${name}`}
          style={{ ...inputStyle, flex: 1 }}
        />
      </div>

      {/* Image settings (only when image is set) */}
      {bg.image && (
        <>
          <div style={rowStyle}>
            <span style={labelStyle}>Size</span>
            <select
              value={bg.size}
              onChange={handleSize}
              disabled={readOnly}
              data-testid={`bg-size-${name}`}
              style={{ ...selectStyle, flex: 1 }}
            >
              {SIZE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div style={rowStyle}>
            <span style={labelStyle}>Position</span>
            <select
              value={bg.position}
              onChange={handlePosition}
              disabled={readOnly}
              data-testid={`bg-position-${name}`}
              style={{ ...selectStyle, flex: 1 }}
            >
              {POSITION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div style={rowStyle}>
            <span style={labelStyle}>Repeat</span>
            <select
              value={bg.repeat}
              onChange={handleRepeat}
              disabled={readOnly}
              data-testid={`bg-repeat-${name}`}
              style={{ ...selectStyle, flex: 1 }}
            >
              {REPEAT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </>
      )}
    </FieldLabel>
  );
}
